import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { careersApi, settingsApi } from '../services/api';
import { Rocket, Zap, Users } from 'lucide-react';

const CareersPage = () => {
  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    position_interest: '',
    message: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvUrl, setCvUrl] = useState('');
  const [cvError, setCvError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [careersEmail, setCareersEmail] = useState('careers@orderflow.co.ke');

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPositions = async () => {
      try {
        const res = await careersApi.getPositions();
        setPositions(res || []);
      } catch (err) {
        console.error('Failed to fetch positions:', err);
      } finally {
        setPositionsLoading(false);
      }
    };
    fetchPositions();
    settingsApi.getPublic()
      .then(data => { if (data?.careersEmail) setCareersEmail(data.careersEmail); })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCvChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      setCvError('Please upload a PDF or Word document (.pdf, .doc, .docx)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setCvError('File size must be under 5MB');
      return;
    }

    setCvError('');
    setCvFile(file);
    setCvUploading(true);

    try {
      const formData = new FormData();
      formData.append('cv', file);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/careers/upload-cv`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Upload failed');
      }
      const data = await res.json();
      setCvUrl(data.url);
    } catch (err) {
      setCvError(err.message || 'CV upload failed. Please try again or email your CV directly.');
      setCvUrl('');
    } finally {
      setCvUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await careersApi.apply({ ...form, cv_url: cvUrl || null });
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.error || 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const parseDescription = (text) => {
    const headers = [
      'Key Responsibilities', 'Qualifications & Requirements',
      'Qualifications', 'Requirements', 'Experience',
      'Education', 'Soft Skills', 'Skills',
      'Method of Application', 'How to Apply'
    ];
    const pattern = new RegExp(
      `(${headers.map(h => h.replace(/&/g, '&')).join('|')})`,
      'g'
    );
    const parts = text.split(pattern).filter(Boolean);
    if (parts.length <= 1) return [{ heading: null, content: text }];
    const sections = [];
    for (let i = 0; i < parts.length; i++) {
      if (headers.includes(parts[i])) {
        sections.push({ heading: parts[i], content: parts[i + 1]?.trim() || '' });
        i++;
      }
    }
    return sections.length ? sections : [{ heading: null, content: text }];
  };

  const parseBullets = (text) => {
    const bullets = text.split(/(?<=\.)\s+(?=[A-Z])/).map(s => s.replace(/^:\s*/, '').trim()).filter(Boolean);
    return bullets.length > 1 ? bullets : [text];
  };

  return (
    <div className="min-h-screen font-sans bg-gray-50">
      <Header />

      {/* Hero */}
      <div className="bg-primary py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Status pill */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent border border-accent/30 text-xs font-medium mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
            </span>
            Now building the team
          </div>

          {/* Heading — serif with accent italics */}
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
            We're looking for{' '}
            <span className="text-accent italic">someone who shows up</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-base leading-relaxed mb-8">
            At OrderFlow, we're building Kenya's order-to-delivery infrastructure — join us.
            We are looking for passionate, driven individuals to grow with us.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="#apply"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-dark text-white text-sm font-semibold rounded-lg hover:brightness-90 transition"
            >
              Apply now
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a
              href="#positions"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-500 text-gray-300 text-sm font-semibold rounded-lg hover:bg-white/10 transition"
            >
              See open roles
            </a>
          </div>

          {/* Stats row — placeholder values */}
          <div className="mt-12 pt-10 border-t border-gray-800">
            <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
              <div className="text-center">
                <p className="font-serif text-3xl font-bold text-white">10+</p>
                <p className="text-xs text-gray-500 mt-1">Open Roles</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-3xl font-bold text-white">5</p>
                <p className="text-xs text-gray-500 mt-1">Locations</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-3xl font-bold text-white">50+</p>
                <p className="text-xs text-gray-500 mt-1">Team Members</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 space-y-12">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-900">Home</Link>
          <span>›</span>
          <span className="text-gray-900">Careers</span>
        </nav>

        {/* Why join us */}
        <div className="bg-white rounded-2xl shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Why Work With Us?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Rocket, title: 'Growth First', desc: 'We invest in our team. Every role is a launchpad, not a ceiling.' },
              { icon: Zap, title: 'Built for Speed', desc: 'Work at the pace of real deliveries, real customers, real impact.' },
              { icon: Users, title: 'Inclusive Culture', desc: 'We celebrate diversity and every voice matters here.' },
            ].map(item => (
              <div key={item.title} className="bg-white rounded-2xl shadow-sm p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-9 h-9 rounded-lg bg-accent-dark/15 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-accent-dark" />
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Open Positions */}
        <div id="positions">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">Open Positions</h2>
          {positionsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-dark"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {positions.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">No open positions</p>
                  <p className="text-lg font-semibold text-gray-900 mb-1">
                    We're always looking for great people
                  </p>
                  <p className="text-sm text-gray-500 mb-5">
                    Send us your CV and we'll keep you in mind for future roles.
                  </p>
                  <a
                    href="#apply"
                    className="inline-block px-5 py-2 bg-accent-dark text-white text-sm font-semibold rounded-lg hover:brightness-90 transition"
                  >
                    Send your CV
                  </a>
                </div>
              ) : (
                positions.map((pos) => (
                  <div key={pos.id} className="bg-white rounded-2xl shadow-sm p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="text-base font-bold text-gray-900">{pos.title}</h3>
                        <span className="px-2 py-0.5 rounded-full bg-accent-dark/10 text-accent-dark text-xs font-medium">
                          {pos.type}
                        </span>
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                          📍 {pos.location}
                        </span>
                      </div>
                      {(() => {
                        const sections = parseDescription(pos.description);
                        const isLong = pos.description.length > 300;
                        const isExpanded = expandedIds.has(pos.id);
                        const visibleSections = isLong && !isExpanded
                          ? sections.slice(0, 1)
                          : sections;
                        return (
                          <div className="mt-2">
                            {visibleSections.map((section, idx) => {
                              const bullets = parseBullets(section.content);
                              return (
                                <div key={idx}>
                                  {section.heading && (
                                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mt-3 mb-1">
                                      {section.heading}
                                    </h4>
                                  )}
                                  {bullets.length > 1 ? (
                                    <ul className="list-disc list-inside space-y-0.5">
                                      {bullets.map((b, i) => (
                                        <li key={i} className="text-sm text-gray-500">{b}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-sm text-gray-500 leading-relaxed">{section.content}</p>
                                  )}
                                </div>
                              );
                            })}
                            {isLong && (
                              <button
                                onClick={() => setExpandedIds(prev => {
                                  const next = new Set(prev);
                                  next.has(pos.id) ? next.delete(pos.id) : next.add(pos.id);
                                  return next;
                                })}
                                className="text-xs text-accent-dark hover:text-accent-dark/80 font-medium mt-2 underline"
                              >
                                {isExpanded ? 'Show less' : 'Read more'}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      <div className="mt-4 text-right">
                        <a
                          href="#apply"
                          onClick={() => setForm(prev => ({ ...prev, position_interest: pos.title }))}
                          className="inline-block px-5 py-2 bg-accent-dark text-white text-sm font-semibold rounded-lg hover:brightness-90 transition text-center"
                        >
                          Apply Now
                        </a>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div className="bg-accent-dark/10 border border-accent-dark/20 rounded-2xl p-5 text-center">
                <p className="text-sm text-accent-dark/80">
                  Don't see a role that fits?{' '}
                  <span className="font-semibold">Send us your CV anyway</span>{' '}
                  — we are always looking for talented people to join our growing team.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Application Form */}
        <div id="apply" className="bg-white rounded-2xl shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Apply Now</h2>
          <p className="text-sm text-gray-500 mb-6">
            Fill in the form below or email your application directly to{' '}
            <a
              href={`mailto:${careersEmail}`}
              className="text-accent-dark hover:underline font-medium"
            >
              {careersEmail}
            </a>
            {' '}with your CV attached.
          </p>

          {submitted ? (
            <div className="bg-accent-dark/10 border border-accent-dark/20 rounded-xl p-6 text-center">
              <h3 className="text-lg font-bold text-accent-dark mb-1">
                Application Received!
              </h3>
              <p className="text-sm text-accent-dark/70">
                Thank you for your interest in joining OrderFlow. We will
                review your application and be in touch within 5–7 business
                days.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Jane Wanjiru"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-dark/20 focus:border-accent-dark"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="jane@example.com"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-dark/20 focus:border-accent-dark"
                  />
                </div>
              </div>

              {/* Phone + Position */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="e.g. 0712 345 678"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-dark/20 focus:border-accent-dark"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position of Interest
                  </label>
                  <select
                    name="position_interest"
                    value={form.position_interest}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-dark/20 focus:border-accent-dark"
                  >
                    <option value="">Select a position...</option>
                    {positions.map(p => (
                      <option key={p.id} value={p.title}>{p.title}</option>
                    ))}
                    <option value="General Application">General Application</option>
                  </select>
                </div>
              </div>

              {/* Cover Letter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cover Letter / Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us about yourself, your experience, and why you want to join OrderFlow..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-dark/20 focus:border-accent-dark resize-none"
                />
              </div>

              {/* CV Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload CV
                </label>
                <div className="border border-dashed border-gray-300 rounded-lg px-4 py-5 text-center hover:border-accent-dark/40 transition">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleCvChange}
                    className="hidden"
                    id="cv-upload"
                  />
                  <label htmlFor="cv-upload" className="cursor-pointer">
                    {cvUploading ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-accent-dark">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-accent-dark"></div>
                        Uploading CV...
                      </div>
                    ) : cvUrl ? (
                      <div className="text-sm text-accent-dark font-medium">
                        ✅ CV uploaded: {cvFile?.name}
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500">
                          <span className="text-accent-dark font-semibold">Click to upload</span>
                          {' '}your CV
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PDF, DOC, or DOCX — max 5MB
                        </p>
                      </div>
                    )}
                  </label>
                </div>
                {cvError && (
                  <p className="text-xs text-red-500 mt-1">{cvError}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Or email your CV to{' '}
                  <a
                    href={`mailto:${careersEmail}`}
                    className="text-accent-dark hover:underline"
                  >
                    {careersEmail}
                  </a>
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || cvUploading}
                className="w-full bg-accent-dark text-white font-semibold py-3 rounded-lg hover:bg-[#00626c] transition disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          )}
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default CareersPage;